import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle, Download, Share2, ArrowLeft, Loader2, Copy } from "lucide-react";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuccessPage = () => {
  const { letterId } = useParams();
  const navigate = useNavigate();
  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchLetter();
  }, [letterId]);

  const fetchLetter = async () => {
    try {
      const response = await axios.get(`${API}/guarantee-letter/${letterId}`);
      setLetter(response.data);
    } catch (error) {
      console.error("Error fetching letter:", error);
      toast.error("Failed to load guarantee letter");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      // Open PDF in new tab which will trigger download
      const pdfUrl = `${API}/guarantee-letter/${letterId}/pdf`;
      window.open(pdfUrl, "_blank");
      toast.success("PDF opened in new tab");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleSendWhatsApp = () => {
    const passengerNames = letter?.passengers?.map(p => p.full_name).join(", ") || "Applicant";
    const passengerCount = letter?.passengers?.length || 1;
    const message = encodeURIComponent(
      `*Guarantee Letter - ${letter?.reference_number}*\n\n` +
      `Applicant${passengerCount > 1 ? 's' : ''}: ${passengerNames}\n` +
      `Total Applicants: ${passengerCount}\n` +
      `Visa Type: ${letter?.visa_type}\n` +
      `Guarantor: ${letter?.guarantor?.full_name}\n` +
      `Date: ${new Date(letter?.created_at).toLocaleDateString()}\n\n` +
      `This is a liability confirmation for UAE visa services from White Sky Travel and Tourism LLC.`
    );
    
    // Send to White Sky Travel WhatsApp number
    window.open(`https://api.whatsapp.com/send?phone=97142202133&text=${message}`, "_blank");
  };

  const copyReferenceNumber = () => {
    navigator.clipboard.writeText(letter?.reference_number || "");
    toast.success("Reference number copied");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </header>

      <main className="flex-1 py-12">
        <div className="max-w-2xl mx-auto px-4">
          {/* Success Message */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 stamp-animation">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-3">
              Application Submitted Successfully
            </h1>
            <p className="text-slate-600">
              Your guarantee letter has been created and is ready for download.
            </p>
          </div>

          {/* Reference Card */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-500">Reference Number</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyReferenceNumber}
                data-testid="copy-reference-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-2xl font-mono font-bold text-slate-900" data-testid="reference-number">
              {letter?.reference_number}
            </p>
          </div>

          {/* Details Card */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
            <h2 className="font-semibold text-slate-900 mb-4">Application Details</h2>
            <div className="space-y-3 text-sm">
              {/* Show all passengers */}
              <div>
                <span className="text-slate-500">Applicant(s)</span>
                <div className="mt-1 space-y-1">
                  {letter?.passengers?.map((p, index) => (
                    <div key={index} className="flex justify-between bg-slate-50 p-2 rounded">
                      <span className="text-slate-900 font-medium">{p.full_name}</span>
                      <span className="text-slate-500 font-mono text-xs">{p.passport_number}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Guarantor</span>
                <span className="text-slate-900 font-medium">{letter?.guarantor?.full_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Visa Type</span>
                <span className="text-slate-900 font-medium">{letter?.visa_type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Date</span>
                <span className="text-slate-900 font-medium">
                  {new Date(letter?.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 rounded-sm"
              data-testid="download-pdf-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>

            <Button
              variant="outline"
              onClick={handleSendWhatsApp}
              className="w-full h-12 rounded-sm bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
              data-testid="send-whatsapp-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Send via WhatsApp
            </Button>

            <Button
              variant="ghost"
              onClick={() => navigate("/form")}
              className="w-full h-12"
              data-testid="new-application-btn"
            >
              Create Another Application
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SuccessPage;
